import { useContext, useState, useEffect } from 'react';
import { Search, Eye, Filter, X, Target, Users, ChevronDown, MapPin, Calendar, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuthContext from '../../../../../context/AuthContext';
import { useToast } from '../../../../../context/ToastContext';
import Pagination from '../../../../../components/Pagination';
import publishedAuditPlanService from '../../../../../services/audit_services/publishedAuditPlanService';

export default function PublishedAuditPlansList() {
  const { authTokens, hasAnyRole } = useContext(AuthContext);
  const { showError } = useToast();

  // Check if user has permission to view published audit plans
  const canViewPublishedPlans = hasAnyRole(['AuditManager', 'AuditUser']);

  const [publishedPlans, setPublishedPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    search: '',
    program: '',
    ordering: 'newest'
  });

  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [showFiltersSection, setShowFiltersSection] = useState(false);

  useEffect(() => {
    fetchPublishedAuditPlans();
  }, [pagination.page, pagination.page_size]);

  const fetchPublishedAuditPlans = async () => {
    if (!canViewPublishedPlans) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await publishedAuditPlanService.getPublishedAuditPlans(
        authTokens.access, 
        filters, 
        { page: pagination.page, page_size: pagination.page_size }
      );
      
      if (result.success) {
        setPublishedPlans(result.data.results);
        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        console.error('Error fetching published audit plans:', result.error);
        showError(result.error || "Erreur lors du chargement des plans d'audit publiés");
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching published audit plans:', error);
      showError("Erreur lors du chargement des plans d'audit publiés");
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      program: '',
      ordering: 'newest'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchPublishedAuditPlans(), 0);
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchPublishedAuditPlans();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      released: { bg: 'bg-green-100', text: 'text-green-800', label: 'Diffusé' },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé' },
      published: { bg: 'bg-green-100', text: 'text-green-800', label: 'Publié' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Clôturé' },
      achieved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Réalisé' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (!canViewPublishedPlans) {
    return (
      <div className="text-center py-12">
        <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Accès refusé</p>
        <p className="text-sm text-gray-500 mt-2">
          Vous devez avoir le rôle AuditManager ou AuditUser et être partie prenante du programme d'audit pour consulter les plans d'audit publiés
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des plans d'audit publiés...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plans d'audit publiés</h1>
          <p className="text-sm text-gray-600 mt-1">Consultez les plans d'audit publiés pour lesquels vous êtes impliqué comme partie prenante</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {/* Search Bar */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher des plans d'audit publiés..."
              value={filters.search}
              onChange={(e) => {
                handleFilterChange('search', e.target.value);
                // Trigger search after a short delay
                clearTimeout(window.searchTimeout);
                window.searchTimeout = setTimeout(() => {
                  applyFilters();
                }, 500);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFiltersSection(!showFiltersSection)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFiltersSection ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Filters Section */}
        {showFiltersSection && (
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Program Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Programme</label>
                <input
                  type="text"
                  placeholder="Filtrer par programme..."
                  value={filters.program}
                  onChange={(e) => handleFilterChange('program', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Sorting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Trier par</label>
                <select
                  value={filters.ordering}
                  onChange={(e) => handleFilterChange('ordering', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="newest">Plus récents publiés</option>
                  <option value="oldest">Plus anciens publiés</option>
                  <option value="title_asc">Titre (A-Z)</option>
                  <option value="title_desc">Titre (Z-A)</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex items-center justify-end space-x-3 mt-4">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="h-4 w-4 mr-2" />
                Réinitialiser
              </button>
              <button
                onClick={applyFilters}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Appliquer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Published Plans Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {publishedPlans.length === 0 ? (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun plan d'audit publié trouvé</p>
            <p className="text-sm text-gray-500 mt-2">
              Les plans d'audit publiés pour lesquels vous êtes impliqué comme partie prenante apparaîtront ici
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Détails de l'audit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Programme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Informations de publication
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {publishedPlans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{plan.title}</p>
                        <p className="text-xs text-gray-500 mt-1">Périmètre: {plan.scope}</p>
                        <p className="text-xs text-gray-500">Réf: {plan.reference}</p>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <MapPin className="h-3 w-3 mr-1" />
                          {plan.location}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {plan.program ? (
                        <div>
                          <p className="text-sm text-green-600 font-medium">{plan.program.title}</p>
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <Building2 className="h-3 w-3 mr-1" />
                            {plan.program.status}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Aucun programme</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        <div className="flex items-center mb-1">
                          <Calendar className="h-3 w-3 mr-1" />
                          <span className="font-medium">Publié :</span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {plan.publish_date ? new Date(plan.publish_date).toLocaleDateString('fr-FR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }) : 'Non disponible'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Par : {plan.created_by || 'Inconnu'}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {getStatusBadge(plan.status)}
                        <div className="flex items-center text-xs text-green-600">
                          <span className="w-2 h-2 bg-green-600 rounded-full mr-1"></span>
                          Publié
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        {/* View Details */}
                        <Link
                          to={`/dashboard/audit/published-plans/${plan.id}`}
                          className="text-green-600 hover:text-green-900"
                          title="Voir les détails"
                        >
                          <Eye className="h-5 w-5" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {publishedPlans.length > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          pageSize={pagination.page_size}
          onPageSizeChange={(size) => setPagination(prev => ({ ...prev, page_size: size, page: 1 }))}
          totalItems={pagination.count}
        />
      )}
    </div>
  );
}